import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BrandLogo } from "@/components/BrandLogo";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, FileCheck2, GraduationCap, Handshake,
  ClipboardList, Mail, Phone, MapPin, Layers, Eye, PlayCircle, Link2, Check,
} from "lucide-react";

const BANNERS = ["/banner1.png", "/banner2.png", "/banner3.png", "/banner4.png", "/banner5.png"];
const ESD = "/esd.png";
// Paste the SMME instructional video MP4 URL below to embed it (leave "" to show the placeholder).
const SMME_VIDEO_URL = "";

const DOCS = [
  "Valid BBBEE Certificate or Affidavit",
  "SARS Tax Clearance / PIN",
  "CIPC Company Registration (CK / COR)",
  "Proof of Address",
  "COIDA",
  "Company Profile",
];

export default function Landing() {
  const [slide, setSlide] = useState(0);
  const [visits, setVisits] = useState(null);
  const [videoUrl, setVideoUrl] = useState(SMME_VIDEO_URL);
  const [buyerVideoUrl, setBuyerVideoUrl] = useState("");
  const [copied, setCopied] = useState("");

  const copyLink = async (key, url) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied(""), 2000);
  };

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % BANNERS.length), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let active = true;
    const recorded = sessionStorage.getItem("ws_visit_recorded");
    const call = recorded ? api.get("/visits") : api.post("/visits");
    call.then((r) => { if (active) setVisits(r.data.count); sessionStorage.setItem("ws_visit_recorded", "1"); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    if (!SMME_VIDEO_URL) api.get("/smme-video").then((r) => { if (active && r.data.available) setVideoUrl(`${api.defaults.baseURL}/smme-video/stream`); }).catch(() => {});
    api.get("/buyer-video").then((r) => { if (active && r.data.available) setBuyerVideoUrl(`${api.defaults.baseURL}/buyer-video/stream`); }).catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <div className="min-h-screen scroll-smooth bg-slate-50" id="top">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <BrandLogo />
          <nav className="hidden items-center gap-7 md:flex" data-testid="landing-nav">
            <a href="#top" className="text-sm font-semibold text-slate-700 transition-colors hover:text-amber-600" data-testid="nav-home">Home</a>
            <Link to="/register" className="text-sm font-semibold text-slate-700 transition-colors hover:text-amber-600" data-testid="nav-create-account">Create Account</Link>
            <a href="#about" className="text-sm font-semibold text-slate-700 transition-colors hover:text-amber-600" data-testid="nav-about">About</a>
            <a href={`${api.defaults.baseURL}/manual`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-slate-700 transition-colors hover:text-amber-600" data-testid="nav-manual">User Manual</a>
            <Link to="/contact" className="text-sm font-semibold text-slate-700 transition-colors hover:text-amber-600" data-testid="nav-contact">Contact</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="nav-login-button" className="font-semibold">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" data-testid="nav-register-button" className="bg-slate-900 font-semibold hover:bg-slate-800">
                Register
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-blue-950">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide}
            src={BANNERS[slide]}
            alt="Wonderstone mine operations"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 0.85, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.2 }, scale: { duration: 6, ease: "linear" } }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950/85 via-blue-900/55 to-blue-900/20" />
        <div className="absolute inset-0 bg-blue-950/10" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-amber-400">
              <Layers className="h-3.5 w-3.5" /> Enterprise & Supplier Development
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Do business with <span className="text-amber-500">Wonderstone Mine</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300">
              Our SMME Portal gives local small, medium and micro enterprises fair access to
              tenders, RFQs and procurement opportunities. Register your business, stay compliant,
              and get notified the moment new opportunities go live.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" data-testid="hero-register-button" className="gap-2 bg-amber-600 font-semibold hover:bg-amber-700">
                  Register your SMME <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" data-testid="hero-login-button" className="border-white/30 bg-white/5 font-semibold text-white hover:bg-white/10 hover:text-white">
                  Buyer / Supplier Sign In
                </Button>
              </Link>
            </div>
          </motion.div>

          <div className="mt-12 flex items-center gap-2.5" data-testid="hero-slide-dots">
            {BANNERS.map((banner, i) => (
              <button
                key={banner}
                onClick={() => setSlide(i)}
                data-testid={`hero-dot-${i}`}
                aria-label={`Show slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === slide ? "w-8 bg-amber-500" : "w-4 bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Bento grid */}
      <section id="about" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-4xl" data-testid="about-us">
          <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl">
            Industrial Mineral &amp; Pyrophyllite Supplier South Africa
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600">
            <p>Wonderstone Ltd is a mining company wholly owned by Assore Ltd and has been mining a uniquely pure pyrophyllite deposit since 1936. The Wonderstone mine is situated approximately 300 kilometres west of Johannesburg, near the town of Ottosdal in South Africa.</p>
            <p>Wonderstone is a leading pyrophyllite supplier South Africa, specialising in industrial minerals and advanced ceramic material solutions for demanding industrial applications.</p>
            <p>With decades of experience in pyrophyllite mining and processing, Wonderstone supports industries requiring reliable technical materials, thermal stability and precision manufacturing performance. Since its inception in 1935, Wonderstone Ltd has built a reputation for strong ethical business practices, innovation and respect for confidentiality.</p>
            <p>The company continues to invest in Research &amp; Development projects focused on expanding the industrial applications and performance capabilities of pyrophyllite. As new uses and advancements for pyrophyllite continue to emerge, Wonderstone remains well positioned for the future.</p>
            <p>Based on current mining rates, market demand and available in-situ resources, the mine has an estimated remaining life exceeding 30 years.</p>
            <p>Wonderstone continues to support local and international industries with high-quality pyrophyllite materials suited to technical ceramics, refractories and advanced manufacturing applications.</p>
            <p className="font-display text-lg font-bold text-amber-600">Discover the wonder of Wonderstone.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          <div className="md:col-span-7 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <img src={ESD} alt="Enterprise supplier development" className="h-48 w-full object-cover" />
            <div className="p-7">
              <GraduationCap className="h-8 w-8 text-amber-600" />
              <h3 className="mt-3 font-display text-2xl font-bold text-slate-900">ESD Development Programme</h3>
              <p className="mt-2 leading-relaxed text-slate-600">
                Through our Enterprise & Supplier Development initiatives we upskill local businesses in
                tendering, costing, pricing and financial management — helping SMMEs meet the requirements
                to enter and grow within the Wonderstone supply chain.
              </p>
            </div>
          </div>

          <div className="md:col-span-5 rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <FileCheck2 className="h-8 w-8 text-amber-600" />
            <h3 className="mt-3 font-display text-xl font-bold text-slate-900">Mandatory Documents</h3>
            <p className="mt-1 text-sm text-slate-500">Prepare these before you register:</p>
            <ul className="mt-4 space-y-2.5">
              {DOCS.map((d) => (
                <li key={d} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {d}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-6 rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <Handshake className="h-8 w-8 text-amber-600" />
            <h3 className="mt-3 font-display text-xl font-bold text-slate-900">Fair Procurement</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Buyers publish RFQs, RFIs, RFPs and tenders on a single transparent platform. Registered
              suppliers respond directly and track the outcome of every submission.
            </p>
          </div>

          <div id="contact" className="md:col-span-6 scroll-mt-20 rounded-2xl border border-slate-900 bg-slate-900 p-7 text-white shadow-sm">
            <ClipboardList className="h-8 w-8 text-amber-500" />
            <h3 className="mt-3 font-display text-xl font-bold">Portal Support</h3>
            <div className="mt-4 space-y-2.5 text-sm text-slate-300">
              <p className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-amber-500" /> support@wonderstonesmmeportal.com</p>
              <p className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-amber-500" /> +27 (0)73 8377 509</p>
              <p className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-amber-500" /> Ottosdal, North West, South Africa</p>
            </div>
          </div>
        </div>
      </section>

      <section id="video" className="scroll-mt-20 border-t border-slate-200 bg-white py-16 sm:py-20" data-testid="smme-video-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Getting started</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 sm:text-4xl">Video Instructions</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">
              Short walkthroughs showing SMMEs and buyers how to register, set up their profile and use the portal. Need help? Our support desk is below.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div data-testid="smme-video-block">
              <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><GraduationCap className="h-5 w-5 text-amber-600" /> For SMMEs / Suppliers</h3>
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 shadow-lg" style={{ aspectRatio: "16 / 9" }} data-testid="smme-video-player">
                {videoUrl ? (
                  <video controls playsInline className="h-full w-full" data-testid="smme-video-el">
                    <source src={videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center text-center" data-testid="smme-video-placeholder">
                    <PlayCircle className="h-14 w-14 text-amber-500" />
                    <p className="mt-3 font-display text-lg font-bold text-white">Video guide coming soon</p>
                    <p className="mt-1 max-w-xs px-4 text-sm text-slate-400">The SMME walkthrough will appear here.</p>
                  </div>
                )}
              </div>
              {videoUrl && (
                <button type="button" onClick={() => copyLink("smme", videoUrl)} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-amber-500 hover:text-amber-600" data-testid="smme-video-copy-link">
                  {copied === "smme" ? (<><Check className="h-4 w-4 text-emerald-600" /> Link copied!</>) : (<><Link2 className="h-4 w-4 text-amber-600" /> Copy video link</>)}
                </button>
              )}
            </div>

            <div data-testid="buyer-video-block">
              <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Handshake className="h-5 w-5 text-amber-600" /> For Buyers</h3>
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 shadow-lg" style={{ aspectRatio: "16 / 9" }} data-testid="buyer-video-player">
                {buyerVideoUrl ? (
                  <video controls playsInline className="h-full w-full" data-testid="buyer-video-el">
                    <source src={buyerVideoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center text-center" data-testid="buyer-video-placeholder">
                    <PlayCircle className="h-14 w-14 text-amber-500" />
                    <p className="mt-3 font-display text-lg font-bold text-white">Video guide coming soon</p>
                    <p className="mt-1 max-w-xs px-4 text-sm text-slate-400">The buyer walkthrough will appear here.</p>
                  </div>
                )}
              </div>
              {buyerVideoUrl && (
                <button type="button" onClick={() => copyLink("buyer", buyerVideoUrl)} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-amber-500 hover:text-amber-600" data-testid="buyer-video-copy-link">
                  {copied === "buyer" ? (<><Check className="h-4 w-4 text-emerald-600" /> Link copied!</>) : (<><Link2 className="h-4 w-4 text-amber-600" /> Copy video link</>)}
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center justify-between gap-4 rounded-2xl border border-slate-900 bg-slate-900 p-6 text-white shadow-sm sm:flex-row" data-testid="video-support-bar">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span className="font-display font-bold">Need a hand?</span>
              <a href="mailto:Support@wonderstonesmmeportal.com" className="flex items-center gap-2 text-slate-200 transition-colors hover:text-amber-400" data-testid="video-support-email"><Mail className="h-4 w-4 text-amber-500" /> Support@wonderstonesmmeportal.com</a>
              <a href="tel:+27738377509" className="flex items-center gap-2 text-slate-200 transition-colors hover:text-amber-400" data-testid="video-support-phone"><Phone className="h-4 w-4 text-amber-500" /> Tel. 073 8377 509</a>
            </div>
            <a href={`${api.defaults.baseURL}/manual`} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-amber-400" data-testid="video-manual-link"><FileCheck2 className="h-4 w-4" /> Download the User Manual</a>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-slate-950 py-10 text-center text-sm text-slate-400">
        <div className="mx-auto mb-4 flex w-fit items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-4 py-1.5 text-slate-300" data-testid="visitor-counter">
          <Eye className="h-4 w-4 text-amber-500" />
          <span className="font-semibold text-white" data-testid="visitor-count">{visits == null ? "…" : visits.toLocaleString()}</span>
          <span className="text-xs uppercase tracking-wide text-slate-400">site visits</span>
        </div>
        <p>© {new Date().getFullYear()} Wonderstone Mine SMME Portal · Supplier & Enterprise Development</p>
        <a href={`${api.defaults.baseURL}/manual`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 hover:underline" data-testid="footer-manual">
          <FileCheck2 className="h-4 w-4" /> Download the User Manual (PDF)
        </a>
      </footer>
    </div>
  );
}
